import React, { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Link, useNavigate } from 'react-router-dom';
import useInput, { useCheckbox } from 'hooks/input.hooks';
import authenticationService from 'services/authentication';
import ColorCheckbox from 'components/ColorCheckbox';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Colors from 'constants/colors';
import {
  OK, USER_REMEMBER_LOCAL_STORE,
} from 'constants';
import AuthLayout from '../AuthLayout';
import useStyles from './style';

function Login() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const user = JSON.parse(localStorage.getItem(USER_REMEMBER_LOCAL_STORE));
  const { value: email, onChange: onChangeEmail } = useInput(
    user ? user.email : '',
  );
  const { value: password, onChange: onChangePassword } = useInput(
    user ? user.password : '',
  );
  const [messErr, setMessErr] = useState('');
  const { value: checked, onChange: onChangeChecked } = useCheckbox(!!user);
  const { value: errorMess, setValue: setError } = useCheckbox(false);
  const { value: isSubmit, setValue: setSubmit } = useCheckbox(false);
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => {
    const currentUser = authenticationService.currentUserValue;
    if (currentUser) {
      navigate('/admin');
    }
  }, [navigate]);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  const login = async () => {
    setError(false);
    if (
      !email
      || !password
      || email.trim().length === 0
      || password.trim().length === 0
    ) {
      setError(true);
      setMessErr(t('pleaseInputUsernameAndPassword'));
    } else {
      setSubmit(true);
      try {
        const userData = {
          username: email,
          password,
        };
        const value = await authenticationService.login(userData);
        const { data, status } = value;
        if (status === OK) {
          authenticationService.updateUser(data?.data);
          localStorage.setItem(
            USER_REMEMBER_LOCAL_STORE,
            checked ? JSON.stringify({ email, password }) : null,
          );
          if (data?.data.roles.roleId === 1) {
            navigate('/admin');
            location.reload();
          } else if (data?.data.roles.roleId === 2){
            navigate('/admin/nhap-lieu/du-lieu-sau-benh-tren-lua');
            location.reload();
          } else {
            navigate('/not-found');
          }
        }
      } catch (error) {
        setError(true);
        if (error.response) {
          if (error.response?.data?.sysError?.code === 'user-is-not-active') {
            setMessErr(t('accountIsNotActive'));
          } else {
            setMessErr(t('usernameOrPasswordIncorrect'));
          }
        } else {
          setMessErr(t('serverError'));
        }
      }
      setSubmit(false);
    }
  };

  const handleKeyUp = (e) => {
    const code = e.keyCode ? e.keyCode : e.which;
    const ENTER_KEY_CODE = 13;
    if (code === ENTER_KEY_CODE) {
      login();
    }
  };
  return (
    <AuthLayout>
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        className={classes.containerLogin}
      >
        <Container maxWidth="xs">
          <Typography align="center" className={classes.title} variant="h1">
            {t('login')}
          </Typography>
          <InputLabel className={classes.label} color="secondary">
            {t('username')}
          </InputLabel>
          <OutlinedInput
            fullWidth
            name="email"
            autoFocus
            value={email}
            onChange={onChangeEmail}
            className={classes.field}
            onKeyUp={handleKeyUp}
          />
          <InputLabel
            className={classes.label}
            htmlFor="outlined-adornment-password"
          >
            {t('password')}
          </InputLabel>
          <OutlinedInput
            fullWidth
            name="password"
            id="password"
            className={classes.field}
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            endAdornment={(
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={toggleShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  style={{ color: Colors.primary }}
                >
                  {password && showPassword ? (
                    <Visibility />
                  ) : (
                    <VisibilityOff />
                  )}
                </IconButton>
              </InputAdornment>
            )}
            value={password}
            onChange={onChangePassword}
            onKeyUp={handleKeyUp}
          />
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <FormControlLabel
                className={classes.formCheckbox}
                control={(
                  <ColorCheckbox
                    checked={checked}
                    onChange={onChangeChecked}
                    value="remember"
                    color="primary"
                  />
                )}
                label={t('savePassword')}
              />
            </Grid>
            <Grid item>
              <Link
                to={{
                  pathname: '/forgot-password',
                  state: email,
                }}
                className={classes.hyperlink}
              >
                {t('forgotPassword')}
              </Link>
            </Grid>
          </Grid>

          {errorMess && <Alert severity="error">{messErr}</Alert>}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            className={clsx(classes.submit, {
              [classes.disabled]: isSubmit,
            })}
            onClick={login}
            disabled={isSubmit}
          >
            {isSubmit ? (
              <CircularProgress size={24} color="secondary" />
            ) : (
              t('login')
            )}
          </Button>
        </Container>
      </Grid>
    </AuthLayout>
  );
}

export default Login;
